'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { ProfileModalType } from '@habity/types';
import { useModalStore } from '~/store/useModalStore';
import { getHomePath } from '@habity/services';
import { NestedHome } from '@habity/types';

/**
 * Hook to manage modal states with URL synchronization
 * This hook handles:
 * 1. Initializing modal states from URL parameters
 * 2. Updating URL when modal states change
 * 3. Providing a function to open/close modals with URL updates
 */
export const useModalWithUrl = (home: NestedHome) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    handleOpen,
    galleryOpen,
    galleryImageId,
    floorPlanOpen,
    virtualTourOpen,
    viewingsOpen,
  } = useModalStore();

  // Initialize modal states from URL parameters on mount
  useEffect(() => {
    const hasGalleryParam = typeof searchParams?.get('images') === 'string';
    const hasFloorPlanParam =
      typeof searchParams?.get('floorPlan') === 'string';
    const hasVirtualTourParam =
      typeof searchParams?.get('virtualTour') === 'string';
    const hasViewingsParam = typeof searchParams?.get('viewings') === 'string';

    if (hasGalleryParam) {
      handleOpen({
        modalType: 'gallery',
        open: true,
        imageId: searchParams?.get('images') || undefined,
      });
    }
    if (hasFloorPlanParam) {
      handleOpen({ modalType: 'floorPlan', open: true });
    }
    if (hasVirtualTourParam) {
      handleOpen({ modalType: 'virtualTour', open: true });
    }
    if (hasViewingsParam) {
      handleOpen({ modalType: 'viewings', open: true });
    }
  }, [searchParams, handleOpen]);

  // Update URL based on modal state changes
  const updateUrlFromModalState = ({
    modalType,
    open,
    imageId,
  }: ProfileModalType) => {
    const homePath = getHomePath(home);
    // getHomePath may include ?preview=token for non-indexable homes
    const separator = homePath.includes('?') ? '&' : '?';

    const appendParam = (param: string) =>
      open ? `${homePath}${separator}${param}` : homePath;

    if (modalType === 'gallery') {
      router.replace(appendParam(`images=${imageId || 'true'}`));
    } else if (modalType === 'floorPlan') {
      router.replace(appendParam('floorPlan=true'));
    } else if (modalType === 'virtualTour') {
      router.replace(appendParam('virtualTour=true'));
    } else if (modalType === 'viewings') {
      router.replace(appendParam('viewings=true'));
    }
  };

  // Function to handle opening/closing modals with URL updates
  const handleOpenWithUrlUpdate = (params: ProfileModalType) => {
    handleOpen(params);
    updateUrlFromModalState(params);
  };

  return {
    // Modal states
    galleryOpen,
    galleryImageId,
    floorPlanOpen,
    virtualTourOpen,
    viewingsOpen,

    // Functions
    handleOpenWithUrlUpdate,
  };
};
